import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Checkbox, { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import CheckboxGroup, { CheckboxGroupComponentProps } from "@insite/mobius/CheckboxGroup";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { FieldSetGroupPresentationProps } from "@insite/mobius/utilities/fieldSetProps";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { FC } from "react";
import { css } from "styled-components";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import siteMessage from "@insite/client-framework/SiteMessage";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";

export interface CartLinesListHeaderStyles {
    wrapper?: InjectableCss;
    priceLabelText?: TypographyPresentationProps;
    productCountText?: TypographyPresentationProps;
    checkboxGroup?: FieldSetGroupPresentationProps<CheckboxGroupComponentProps>;
    condensedCheckbox?: CheckboxPresentationProps;
    warningIcon?: IconPresentationProps;
    warningSection?: InjectableCss;
    warningText?: InjectableCss;
    disApprovingWrapper?: InjectableCss;
    disApprovingText?: InjectableCss;
}

export const cartLinesListHeaderStyles: CartLinesListHeaderStyles = {
    wrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            border-top: 1px solid #C2D1D9;
            border-bottom: 1px solid #C2D1D9;
            padding-top: 1.25rem;
            padding-bottom: 1.25rem;
        `,
    },
    disApprovingText: {
        css: css`
            width: 100%;
            color: red;
        `,
    },
      disApprovingWrapper: {
            css: css`
                text-align: center;
                padding: .625rem;
                background-color: white;
                border: 2px solid red;
                margin-bottom: .9375rem;
    
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                            margin-bottom: 0;
                            margin-top: 1.5rem;
                        `,
                            null,
                            null,
                        ],
                        "max",
                    )}
            `,
        },
    productCountText: { 
        css: css`
            color: #002554;
            font-family: Roboto;
            font-size: 1.375rem;
            font-weight: bold;
            letter-spacing: 0;
            line-height: 1.4375rem;

            @media print {
                &.products-heading {
                    width: 42%;
                }
            }
            @media (min-width: 991px) {
                &.products-heading {
                    width: 43%;
                }
            }
            @media screen and (min-width: 768px) and (max-width: 990px) {
                &.products-heading {
                    width: 43%;
                }
            }
            
            @media (max-width: 767px) {
                width: 100%;
            }
        `,
     },
    checkboxGroup: {
        css: css`
            @media print {
                display: none;
            }
        `,
    },
    priceLabelText: {
        css: css `
            color: #005CB9;
            font-family: Lato;
            font-size: 1rem;
            font-weight: bold;
            letter-spacing: .031rem;
            line-height: 1.5rem;

            span {
                font-weight: normal;
            }
            @media (min-width: 991px) {
                &.price-heading {
                    width: 24%;
                }
                &.qty-heading {
                    width: 15%;
                }
                &.subtotal-heading {
                    width: 18%;
                    justify-content: right;
                    text-align: right;
                }
            }
            @media screen and (min-width: 768px) and (max-width: 990px) {
                &.price-heading {
                    width: 12%;
                }
                &.qty-heading {
                    width: 12%;
                }
            }
            @media print {
                &.products-heading {
                    width: 40%;
                }
                &.price-heading {
                    width: 24%;
                }
                &.qty-heading {
                    width: 15%;
                }
                &.subtotal-heading {
                    width: 21%;
                    justify-content: right;
                    text-align: right;
                }
            }

            @media (max-width: 767px) {
                display: none;
            }
        `,
    },
    warningIcon: {
        src: "AlertCircle",
        color: "danger.main",
        css: css`
            margin-right: .625rem;
        `,
    },
    warningSection: {
        css: css`
            display: flex;
            align-items: center;
            border: 2px solid ${getColor("danger.main")};
            padding: .9375rem .625rem;
            margin-bottom: .9375rem;

            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    null,
                    null,
                    css`
                        margin-bottom: 0;
                        margin-top: 1.5625rem;
                    `,
                    null,
                    null,
                ],
                "max",
            )}
        `,
    },
};

const CartLinesListHeader: FC<{
    productsCount: number;
    isCondensed: boolean;
    hideCondensedSelector?: boolean;
    onChangeIsCondensed?: (event: React.SyntheticEvent, value: boolean) => void;
    extendedStyles?: CartLinesListHeaderStyles;
    enableVat?:boolean;
    productsCannotBePurchased?: boolean;
    cartNotPriced?: boolean;
    cartContainsZeroPriceItem: boolean | undefined;
    failedToGetRealTimeInventory?: boolean;
    errorMessage?: string;
    allowZeroPricing:boolean | undefined;
    isQuote?: boolean;
}> = ({ productsCount, onChangeIsCondensed, extendedStyles, isCondensed, hideCondensedSelector,enableVat,
    productsCannotBePurchased, cartNotPriced, cartContainsZeroPriceItem, failedToGetRealTimeInventory, errorMessage,allowZeroPricing, isQuote }) => {
    const defaultErrorMessage =
        "One or more items in your cart cannot be purchased in the online store. Please contact sales at (800) 950-3457";

    const [styles] = React.useState(() => mergeToNew(cartLinesListHeaderStyles, extendedStyles));

    const StyledSection = getStyledWrapper("section");
    const productsLabel = productsCount > 1 ? "Items in this Order" : "Item in this Order";

    return (
        <>
         {productsCannotBePurchased && !isQuote && (
                <StyledSection {...styles.warningSection}>
                    <Icon {...styles.warningIcon}></Icon>
                    <Typography {...styles.warningText}>
                        {siteMessage("Cart_ProductsCannotBePurchased")}
                    </Typography>
                </StyledSection>
            )}
            {cartNotPriced && (
                <StyledSection {...styles.warningSection}>
                    <Icon {...styles.warningIcon}></Icon>
                    <Typography {...styles.warningText}>
                        {siteMessage("Cart_NoPriceAvailableAtCheckout")}
                    </Typography>
                </StyledSection>
            )}
            {failedToGetRealTimeInventory && (
                <StyledSection {...styles.warningSection}>
                    <Icon {...styles.warningIcon}></Icon>
                    <Typography {...styles.warningText}>
                        {siteMessage("Cart_NoInventoryAvailableAtCheckout")}
                    </Typography>
                </StyledSection>
            )}
            {cartContainsZeroPriceItem && !allowZeroPricing &&(
                <StyledWrapper {...styles.disApprovingWrapper}>
                    <Typography {...styles.disApprovingText}>
                        {translate(errorMessage || defaultErrorMessage)}
                    </Typography>
                </StyledWrapper>
            )}
            <StyledWrapper {...styles.wrapper}>
                <Typography {...styles.productCountText} as="h3" className="products-heading">
                    {productsCount} {translate(productsLabel)}
                </Typography>
                <Typography {...styles.priceLabelText} className="price-heading" as="h3">{translate("Price")}</Typography>
                <Typography {...styles.priceLabelText} className="qty-heading" as="h3">{translate("Quantity")}</Typography>
                <Typography {...styles.priceLabelText} className="subtotal-heading" as="h3">{translate("Subtotal") + (enableVat ? ` (${translate("Ex. VAT")})` : "")}</Typography>

            </StyledWrapper>
        </>
    );
};

export default CartLinesListHeader;
